// TradingCommand.js - Clean Refactored Version
// Eliminates enrichment layer, reduces state, simplifies data flow
// Socket → Raw State → Derived State (useMemo) → Components

import socketIOClient from "socket.io-client";
import config from "./config";
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Container, Row, Col, Card, Alert } from "react-bootstrap";

// Services
import { prepareData } from "./service/PredictionAccuracy";
import prepareOrder from "./service/PrepareOrder";
import buildActivities from "./service/BuildActivity";
import getDataAPI from "./service/apiservice";
import { getToken } from "./service/AuthService";

// Components
import ProfessionalCountdownTimer from "./components/timer/ProfessionalCountdownTimer";
import FeedCard from "./components/feedcard/Feedcard";
import MarketChart from "./components/TradingCommand/MarketChart";
import EvaluationTimeline from "./components/TradingCommand/EvaluationTimeline";
import EvaluationDetailModal from "./components/TradingCommand/EvaluationDetailModal";
import PositionDetailModal from "./components/TradingCommand/PositionDetailModal";
import DayPnLSummary from "./components/TradingCommand/DayPnLSummary";
import ActivePositions from "./components/TradingCommand/ActivePositions";
import ClosedPositions from "./components/TradingCommand/ClosedPositions";
import ActivitySummary from "./components/TradingCommand/ActivitySummary";

// Redux
import { useDispatch } from "react-redux";
import {
  addOrder,
  addBars,
  addActivity,
  addPrediction,
  clearOrder,
  clearBars,
  clearActivity,
  clearPrediction,
} from "./service/actions";

const ENDPOINT = config.API_SOCKET_URL;

// ============================================================================
// HEADER COMPONENT
// ============================================================================
const TradingHeader = ({ symbol, close, marketStatus, chartTS }) => (
  <Card className="mb-3" style={{ backgroundColor: "#0a0a0a", color: "white", border: "1px solid #222" }}>
    <Card.Body className="py-2">
      <Row className="align-items-center">
        <Col xs={3}>
          <h5 className="mb-0" style={{ color: "#ffffff" }}>
            {symbol} ${close}
          </h5>
        </Col>
        <Col xs={6} className="text-center">
          <ProfessionalCountdownTimer />
        </Col>
        <Col xs={3} className="text-end">
          <small style={{ color: "#ccc" }}>
            Status:{" "}
            <span className={marketStatus === "open" ? "text-success" : "text-warning"}>
              {marketStatus}
            </span>
          </small>
          <br />
          <small style={{ color: "#ccc" }}>{chartTS}</small>
        </Col>
      </Row>
    </Card.Body>
  </Card>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const TradingCommand = () => {
  // -----------------------------------------------------------------------
  // RAW STATE (From Socket) - Only 3 items
  // -----------------------------------------------------------------------
  const [liveBars, setLiveBars] = useState([]);
  const [rawOrders, setRawOrders] = useState([]);
  const [currentPrice, setCurrentPrice] = useState(0);

  // -----------------------------------------------------------------------
  // UI STATE - Only 4 items
  // -----------------------------------------------------------------------
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [showEvalModal, setShowEvalModal] = useState(false);
  const [showPositionModal, setShowPositionModal] = useState(false);

  // -----------------------------------------------------------------------
  // METADATA - Only 1 item
  // -----------------------------------------------------------------------
  const [marketInfo, setMarketInfo] = useState({
    symbol: "",
    close: "",
    status: "",
    timestamp: "",
    isActive: false,
    error: null,
  });

  // -----------------------------------------------------------------------
  // REFS - For change tracking
  // -----------------------------------------------------------------------
  const prevOrderKeyRef = useRef("");
  const prevHistoryLengthRef = useRef(0);
  const allOrdersRef = useRef([]);

  const dispatch = useDispatch();

  // =======================================================================
  // DERIVED STATE (useMemo) - All computed from raw state
  // =======================================================================

  // 1. ACTIVITIES - Transform orders into activity records
  const activities = useMemo(() => {
    if (!rawOrders || rawOrders.length === 0) {
      console.log("⏸️ No orders to process");
      return [];
    }

    try {
      console.log("🔄 Processing orders into activities:", rawOrders.length);

      const activityArray = [];
      rawOrders.forEach((row) => {
        const [activity, positions] = prepareOrder(row);
        if (activity) {
          activityArray.push(activity);
        }
      });

      if (activityArray.length === 0) {
        console.log("⚠️ No valid activities from orders");
        return [];
      }

      const processedActivities = buildActivities(activityArray);
      console.log("✅ Built activities:", processedActivities.length);

      return processedActivities;
    } catch (err) {
      console.error("❌ Activity processing error:", err);
      return [];
    }
  }, [rawOrders]);

  // 2. POSITIONS - Build complete position list from activities
  const positions = useMemo(() => {
    if (!activities || activities.length === 0) {
      console.log("⏸️ No activities to build positions");
      return [];
    }

    console.log("📊 Building positions from activities");

    // Group activities by trade group
    const tradeGroups = {};
    activities.forEach((activity) => {
      const groupId = activity.group;
      if (!tradeGroups[groupId]) {
        tradeGroups[groupId] = [];
      }
      tradeGroups[groupId].push(activity);
    });

    const allPositions = [];

    Object.keys(tradeGroups).forEach((groupId) => {
      const groupActivities = tradeGroups[groupId].sort(
        (a, b) => new Date(a.created_at_gmt) - new Date(b.created_at_gmt)
      );

      const firstActivity = groupActivities[0];
      const lastActivity = groupActivities[groupActivities.length - 1];
      const openQty = lastActivity.open_qty || 0;

      // Determine position type
      const isLong = firstActivity.side === "buy";
      const entryPrice = parseFloat(firstActivity.price);
      const qty = Math.abs(firstActivity.qty);

      // Find target and stop from orders
      let target = 0;
      let stop = 0;

      rawOrders.forEach((orderRow) => {
        const order = orderRow.order || orderRow;
        if (order.symbol === firstActivity.symbol) {
          const orderTime = new Date(order.created_at);
          const entryTime = new Date(firstActivity.created_at_gmt);

          // Orders created around the same time as entry
          const timeDiff = Math.abs(orderTime - entryTime);
          if (timeDiff < 60000) {
            // Within 1 minute
            const limitPrice = order.limit_price ? parseFloat(order.limit_price) : 0;
            const stopPrice = order.stop_price ? parseFloat(order.stop_price) : 0;

            // Target order
            if (order.type === "limit" && order.side !== firstActivity.side && limitPrice > 0) {
              if ((isLong && limitPrice > entryPrice) || (!isLong && limitPrice < entryPrice)) {
                if (target === 0) target = limitPrice;
              }
            }

            // Stop order
            if ((order.type === "stop" || order.type === "stop_limit") && stopPrice > 0) {
              if ((isLong && stopPrice < entryPrice) || (!isLong && stopPrice > entryPrice)) {
                if (stop === 0) stop = stopPrice;
              }
            }
          }
        }
      });

      // For ACTIVE positions
      if (openQty !== 0) {
        const unrealizedPL = currentPrice > 0
          ? isLong
            ? (currentPrice - entryPrice) * qty
            : (entryPrice - currentPrice) * qty
          : 0;

        const riskAmount = stop > 0 ? Math.abs(entryPrice - stop) * qty : 0;
        const rewardAmount = target > 0 ? Math.abs(target - entryPrice) * qty : 0;
        const rrRatio = riskAmount > 0 ? (rewardAmount / riskAmount).toFixed(2) : 0;

        // Calculate hold time
        const entryTime = new Date(firstActivity.created_at_gmt);
        const now = new Date();
        const holdTimeMin = Math.floor((now - entryTime) / 60000);

        allPositions.push({
          ...lastActivity,
          id: lastActivity.id || lastActivity.a_id,
          a_id: lastActivity.a_id,
          group: groupId,
          symbol: firstActivity.symbol,
          isLong,
          side: isLong ? "buy" : "sell",
          entryPrice,
          currentPrice,
          qty,
          open_qty: openQty,
          target,
          stop,
          tp: target,
          sl: stop,
          unrealizedPL,
          riskAmount,
          rrRatio,
          entryTime: firstActivity.created_at_gmt,
          exitTime: null,
          exitPrice: null,
          holdTimeMin,
          isWin: false,
          exitType: null,
        });
      }
      // For CLOSED positions
      else {
        const exitPrice = parseFloat(lastActivity.price);
        const realizedPL = parseFloat(lastActivity.pl_delta) || 0;
        const tax = parseFloat(lastActivity.tax) || 0;
        const isWin = realizedPL > 0;

        // Calculate hold time
        const entryTime = new Date(firstActivity.created_at_gmt);
        const exitTime = new Date(lastActivity.submitted_at_gmt);
        const holdTimeMin = Math.floor((exitTime - entryTime) / 60000);

        // Determine exit type
        let exitType = "Unknown";
        const activityType = lastActivity.type;

        if (activityType === "stop") {
          exitType = "Stop Hit";
        } else if (activityType === "limit" && lastActivity.side !== firstActivity.side) {
          exitType = "Target Fill";
        } else if (activityType === "market") {
          exitType = "Market Close";
        } else if (lastActivity.side === "sell" && firstActivity.side === "buy") {
          exitType = "Target Fill";
        } else if (lastActivity.side === "buy" && firstActivity.side === "sell") {
          exitType = "Cover";
        } else {
          exitType = "Market Close";
        }

        allPositions.push({
          ...lastActivity,
          id: lastActivity.id || lastActivity.a_id,
          a_id: lastActivity.a_id,
          group: groupId,
          symbol: firstActivity.symbol,
          isLong,
          side: isLong ? "buy" : "sell",
          entryPrice,
          exitPrice,
          currentPrice: exitPrice,
          qty,
          open_qty: openQty,
          target,
          stop,
          tp: target,
          sl: stop,
          unrealizedPL: realizedPL,
          tax,
          riskAmount: 0,
          rrRatio: 0,
          entryTime: firstActivity.created_at_gmt,
          exitTime: lastActivity.submitted_at_gmt,
          holdTimeMin,
          isWin,
          exitType,
        });
      }
    });

    console.log("✅ Built positions:", {
      total: allPositions.length,
      active: allPositions.filter((p) => p.open_qty !== 0).length,
      closed: allPositions.filter((p) => p.open_qty === 0).length,
    });

    return allPositions;
  }, [activities, rawOrders, currentPrice]);

  /*
  // 3. ACTIVE POSITIONS
  const activePositions = useMemo(() => {
    return positions.filter((p) => {
      const openQty = p.open_qty || 0;
      return openQty !== 0;
    });
  }, [positions]);

  // 4. CLOSED POSITIONS
  const closedPositions = useMemo(() => {
    return positions.filter((p) => {
      const openQty = p.open_qty || 0;
      return openQty === 0 && p.exitTime && p.exitPrice;
    });
  }, [positions]);
  */

  const activePositions = useMemo(
    () => positions.filter(p => (p.open_qty ?? 0) !== 0),
     [positions]
   );
  
  const closedPositions = useMemo(
  () => positions.filter(p => (p.open_qty ?? 0) === 0 && p.exitTime && p.exitPrice),
     [positions]
  );

  // 5. METRICS
  const metrics = useMemo(() => {
    const totalUnrealizedPL = activePositions.reduce((sum, pos) => {
      return sum + (parseFloat(pos.unrealizedPL) || 0);
    }, 0);

    const totalRealizedPL = closedPositions.reduce((sum, pos) => {
      return sum + (parseFloat(pos.unrealizedPL) || 0);
    }, 0);

    const dayPL = totalRealizedPL + totalUnrealizedPL;

    const wins = closedPositions.filter((p) => p.isWin).length;
    const winRate = closedPositions.length > 0 
      ? parseFloat(((wins / closedPositions.length) * 100).toFixed(0))
      : 0;

    const avgHoldTime = closedPositions.length > 0
      ? Math.round(
          closedPositions.reduce((sum, p) => sum + (p.holdTimeMin || 0), 0) /
            closedPositions.length
        )
      : 0;

    const bestTrade = closedPositions.length > 0
      ? Math.max(...closedPositions.map((p) => parseFloat(p.unrealizedPL) || 0))
      : 0;

    const worstTrade = closedPositions.length > 0
      ? Math.min(...closedPositions.map((p) => parseFloat(p.unrealizedPL) || 0))
      : 0;

    const totalFees = closedPositions.reduce((sum, p) => {
      return sum + Math.abs(parseFloat(p.tax) || 0);
    }, 0);

    // Return with all values properly initialized to prevent undefined errors
    return {
      dayPL: dayPL || 0,
      realizedPL: totalRealizedPL || 0,
      unrealizedPL: totalUnrealizedPL || 0,
      activePositions: activePositions.length || 0,
      closedTrades: closedPositions.length || 0,
      winRate: winRate || 0,
      avgHoldTime: avgHoldTime || 0,
      bestTrade: bestTrade || 0,
      worstTrade: worstTrade || 0,
      totalFees: totalFees || 0,
    };
  }, [activePositions, closedPositions]);

  // =======================================================================
  // HELPER FUNCTIONS
  // =======================================================================

  const filterChartDataForPosition = useCallback((bars, position) => {
    if (!bars || bars.length === 0 || !position) return [];

    const entryTime = new Date(position.entryTime);
    const exitTime = position.exitTime
      ? new Date(position.exitTime)
      : new Date(Date.now() + 86400000);

    return bars.filter((bar) => {
      const barTime = new Date(bar.t);
      return barTime >= entryTime && barTime <= exitTime;
    });
  }, []);

  const filterEvaluationsForPosition = useCallback((bars, position) => {
    if (!bars || bars.length === 0 || !position) return [];

    const evaluations = [];

    bars.forEach((bar, index) => {
      const decision = bar.eval_decision || bar.decision || bar.signal;
      if (!decision || decision === "SKIP") return;

      const reasoning = bar.eval_reasoning || bar.gen_ai_msg?.Reason || "No reasoning provided";

      const evalType = bar.eval_type || (bar.signal && bar.signal !== "HOLD" ? "PROACTIVE" : "REACTIVE");

      evaluations.push({
        id: `eval-${index}-${bar.t}`,
        type: evalType,
        decision: decision,
        reasoning: reasoning,
        timestamp: bar.t || bar.timestamp,
        confidence: bar.eval_confidence || bar.confidence,
        execution_time: bar.eval_execution_time,
        position_id: bar.eval_position_id || bar.position_id,
        fullData: bar,
      });
    });

    return evaluations;
  }, []);

  // =======================================================================
  // EVENT HANDLERS
  // =======================================================================

  const handleClosePosition = useCallback((position) => {
    console.log("Closing position:", position);

    getDataAPI({}, "trade/liquidate")
      .then((output) => {
        console.log("Close position response:", output);
        alert(`Position closed: ${output}`);
      })
      .catch((err) => {
        console.error("Error closing position:", err);
        setMarketInfo((prev) => ({
          ...prev,
          error: "Failed to close position. Please try again.",
        }));
      });
  }, []);

  const handlePositionSelect = useCallback(
    (position) => {
      console.log("Position selected:", position);

      if (!position) {
        setShowPositionModal(false);
        return;
      }

      setSelectedPosition(position);
      setShowPositionModal(true);
    },
    []
  );

  const handlePositionModalClose = useCallback(() => {
    setShowPositionModal(false);
    setSelectedPosition(null);
  }, []);

  const handleEvaluationSelect = useCallback((evaluation) => {
    console.log("Evaluation selected:", evaluation);
    setSelectedEvaluation(evaluation);
    setShowEvalModal(true);
  }, []);

  const handleViewFullStory = useCallback((position) => {
    console.log("View full story for position:", position);
    setSelectedPosition(position);
    setShowPositionModal(false);
  }, []);

  // =======================================================================
  // SOCKET & DATA EFFECTS
  // =======================================================================

  // 1. SOCKET SETUP
  useEffect(() => {
    const socket = socketIOClient(ENDPOINT, {
      transports: ["websocket", "polling", "flashsocket"],
      transportOption: {
        websocket: {
          extraHeaders: { Authorization: `Bearer ${getToken()}` },
        },
      },
      reconnectionDelay: 1000,
      reconnection: true,
      reconnectionAttempts: 3,
      agent: false,
      upgrade: false,
      rejectUnauthorized: false,
      extraHeaders: { Authorization: `Bearer ${getToken()}` },
    });

    socket.on("connect_error", (err) => {
      setMarketInfo((prev) => ({
        ...prev,
        error: "Connection error. Please check your network.",
      }));
      console.error("Socket connection error:", err);
    });

    // PREDICTION HISTORY
    socket.on("predictionHistoryEvent", (data) => {
      try {
        const rawHistory = JSON.parse(data["data"]);
        const marketStatus = data["market_status"];

        console.log("📊 Prediction event:", rawHistory.length, "bars");

        // Process with eval fields preserved
        const preparedData = rawHistory.map((rawBar) => {
          const processedBar = prepareData(rawBar);

          return {
            ...processedBar,
            eval_decision: rawBar.eval_decision,
            eval_type: rawBar.eval_type,
            eval_reasoning: rawBar.eval_reasoning,
            eval_narrative_summary: rawBar.eval_narrative_summary,
            eval_agent_story: rawBar.eval_agent_story,
            eval_position_id: rawBar.eval_position_id,
            eval_new_stop_loss: rawBar.eval_new_stop_loss,
            eval_new_target: rawBar.eval_new_target,
            eval_pattern_influence: rawBar.eval_pattern_influence,
            eval_adjustment_applied: rawBar.eval_adjustment_applied,
            eval_execution_time: rawBar.eval_execution_time,
            eval_error: rawBar.eval_error,
            position_id: rawBar.position_id || rawBar.eval_position_id,
            decision: rawBar.decision || rawBar.eval_decision || rawBar.signal,
          };
        });

        const slidingData = preparedData.slice(-60);
        setLiveBars(slidingData);

        // Update market info
        if (slidingData.length > 0) {
          const lastData = slidingData[slidingData.length - 1];
          setMarketInfo({
            symbol: lastData.symbol,
            close: lastData.close,
            status: marketStatus,
            timestamp: new Date(lastData.t).toLocaleString("en-US"),
            isActive: true,
            error: null,
          });
          setCurrentPrice(parseFloat(lastData.close) || 0);
        }

        // Redux updates
        dispatch(clearBars());
        dispatch(addBars(slidingData));
        dispatch(clearPrediction());
        dispatch(addPrediction(slidingData));
      } catch (err) {
        console.error("Prediction history error:", err);
        setMarketInfo((prev) => ({
          ...prev,
          error: "Error processing prediction data",
        }));
      }
    });

    // ORDER HISTORY
    socket.on("orderHistoryEvent", (data) => {
      try {
        const rawOrders = JSON.parse(data["data"]);
        console.log("📦 Order event:", rawOrders.length, "orders");

        // Always set orders, even if it's a duplicate
        // This handles initial load case
        if (allOrdersRef.current.length === 0 && rawOrders.length > 0) {
          console.log("🔄 Initial order history load:", rawOrders.length);
          allOrdersRef.current = rawOrders;
          setRawOrders([...rawOrders]);
          return;
        }

        if (rawOrders.length > allOrdersRef.current.length) {
          console.log("🔄 Full order history received");
          allOrdersRef.current = rawOrders;
          setRawOrders([...rawOrders]);
          return;
        }

        const existingOrderIds = new Set(
          allOrdersRef.current.map((o) => o.order?.id).filter(Boolean)
        );
        const newOrders = rawOrders.filter((o) => !existingOrderIds.has(o.order?.id));

        if (newOrders.length > 0) {
          console.log("✨ Adding", newOrders.length, "new orders");
          allOrdersRef.current = [...allOrdersRef.current, ...newOrders];
          setRawOrders([...allOrdersRef.current]);
        }
      } catch (err) {
        console.error("Order history error:", err);
      }
    });

    // TRADE EVENT
    socket.on("tradeEvent", (data) => {
      try {
        const tradeBar = data["data"] ? JSON.parse(data["data"]) : {};
        
        if (tradeBar && tradeBar.t) {
          let dt = new Date(tradeBar.t);
          dt.setSeconds(0);
          dt.setMilliseconds(0);
          tradeBar.t = dt.toISOString().replace(".000Z", ".000000000Z");

          setCurrentPrice(parseFloat(tradeBar.close) || 0);

          setLiveBars((currentBars) => {
            if (currentBars.length === 0 || tradeBar.t > currentBars[currentBars.length - 1].t) {
              return [...currentBars, tradeBar].slice(-60);
            } else if (tradeBar.t === currentBars[currentBars.length - 1].t) {
              const newBars = [...currentBars];
              newBars.pop();
              newBars.push(tradeBar);
              return newBars.slice(-60);
            }
            return currentBars;
          });

          setMarketInfo((prev) => ({
            ...prev,
            close: tradeBar.close,
            symbol: tradeBar.symbol,
            timestamp: new Date(tradeBar.t).toLocaleString("en-US"),
            isActive: true,
          }));
        }
      } catch (err) {
        console.error("Trade event error:", err);
      }
    });

    // Initial requests
    socket.emit("getPredictionHistory", null);
    socket.emit("getOrderHistory", null);

    return () => {
      socket.disconnect();
    };
  }, [dispatch]);

  // 2. REDUX SYNC - Keep Redux in sync with activities
  useEffect(() => {
    if (activities.length > 0) {
      dispatch(clearActivity());
      dispatch(addActivity(activities));
    }
  }, [activities, dispatch]);

  // 3. REDUX SYNC - Keep Redux in sync with orders
  useEffect(() => {
    if (rawOrders.length > 0) {
      dispatch(clearOrder());
      dispatch(addOrder(rawOrders));
    }
  }, [rawOrders, dispatch]);

  /*
  console.table(
    (positions || []).map(p => ({
      id: p.id || p.a_id || p.group,
      symbol: p.symbol,
      open_qty_raw: p.open_qty,
      open_qty_num: Number(p?.open_qty ?? 0),
      consideredOpenByParent: Number(p?.open_qty ?? 0) > 0,
      side: p.side,
      isLong: p.isLong,
    }))
  );*/
  
  // =======================================================================
  // RENDER
  // =======================================================================

  return (
    <div style={{ backgroundColor: "#000000", minHeight: "100vh" }}>
      <style>{`
        body { background-color: #000000 !important; color: #ffffff !important; }
        .card { background-color: #0a0a0a !important; border: 1px solid #222 !important; color: #ffffff !important; }
        .card-body { background-color: #0a0a0a !important; color: #ffffff !important; }
        .card-header { background-color: #0a0a0a !important; border-bottom: 1px solid #222 !important; color: #ffffff !important; }
        .alert { background-color: #1a0000 !important; border-color: #ff4444 !important; color: #ffcccc !important; }
        .text-muted { color: #888 !important; }
        h1, h2, h3, h4, h5, h6, p, span, div { color: inherit !important; }
      `}</style>

      <Container fluid className="py-3">
        <TradingHeader
          symbol={marketInfo.symbol}
          close={marketInfo.close}
          marketStatus={marketInfo.status}
          chartTS={marketInfo.timestamp}
        />

        {marketInfo.error && (
          <Alert variant="danger" className="mb-3">
            {marketInfo.error}
          </Alert>
        )}

        {!marketInfo.isActive ? (
          <Card>
            <Card.Body className="text-center py-5">
              <div className="spinner-border text-primary mb-3" role="status"></div>
              <div className="text-muted">Connecting to trading system...</div>
            </Card.Body>
          </Card>
        ) : (
          <>
            <DayPnLSummary
              dayPL={metrics.dayPL}
              realizedPL={metrics.realizedPL}
              unrealizedPL={metrics.unrealizedPL}
              activePositions={metrics.activePositions}
              closedTrades={metrics.closedTrades}
              winRate={metrics.winRate}
              avgHoldTime={metrics.avgHoldTime}
              bestTrade={metrics.bestTrade}
              worstTrade={metrics.worstTrade}
              totalFees={metrics.totalFees}
            />

            <Row className="mt-3">
              {/* LEFT COLUMN - Active Positions */}
              <Col xs={12} lg={3}>
                <Card style={{ height: "600px", backgroundColor: "#0a0a0a", border: "1px solid #222" }}>
                  <Card.Body className="p-0" style={{ height: "100%", overflow: "hidden" }}>
                    
                    <ActivePositions
                      activities={positions}      // ← {activePositions}
                      liveBars={liveBars} 
                      currentPrice={currentPrice}
                      onClose={handleClosePosition}
                      onDetail={(position) => handlePositionSelect(position)}
                      onSelectPosition={(posId) => {
                        const pos = positions.find((p) => (p.id || p.a_id) === posId);
                        if (pos) setSelectedPosition(pos);
                      }}
                      selectedPositionId={selectedPosition?.id || selectedPosition?.a_id}
                    />
                  </Card.Body>
                </Card>
              </Col>

              {/* RIGHT COLUMN - Chart & Closed Positions */}
              <Col xs={12} lg={9}>
                {/* Market Chart */}
                <Card style={{ backgroundColor: "#0a0a0a", border: "1px solid #222" }}>
                  <Card.Header className="py-2">
                    <h6 className="mb-0" style={{ color: "#ffffff" }}>
                      Market Analysis - {marketInfo.symbol}
                    </h6>
                  </Card.Header>
                  <Card.Body className="p-2">
                    {liveBars.length > 0 ? (
                      <MarketChart data={liveBars} selectedPosition={selectedPosition} />
                    ) : (
                      <div className="d-flex align-items-center justify-content-center" style={{ height: "400px" }}>
                        <div className="text-muted text-center">
                          <div className="spinner-border spinner-border-sm mb-2" role="status"></div>
                          <div>Loading chart data...</div>
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>

                {/* Closed Positions */}
                {closedPositions.length > 0 && (
                  <Card className="mt-2" style={{ maxHeight: "300px", overflow: "hidden", backgroundColor: "#0a0a0a", border: "1px solid #222" }}>
                    <Card.Body className="p-0">
                      <ClosedPositions
                        positions={closedPositions}
                        totalRealized={metrics.realizedPL}
                        tradeCount={metrics.closedTrades}
                      />
                    </Card.Body>
                  </Card>
                )}
              </Col>
            </Row>

            {/* Evaluation Timeline */}
            {liveBars.length > 0 && (
              <div className="mt-3">
                <EvaluationTimeline
                  predictions={liveBars}
                  onEvaluationSelect={handleEvaluationSelect}
                  selectedEvalId={selectedPosition?.id || selectedPosition?.a_id}
                  positions={positions}
                  onPositionSelect={handlePositionSelect}
                />
              </div>
            )}

            {/* Activity Summary */}
            {activities.length > 0 && (
              <div className="mt-3">
                <ActivitySummary data={activities} />
              </div>
            )}
          </>
        )}
      </Container>

      {/* Modals */}
      <EvaluationDetailModal
        evaluation={selectedEvaluation}
        show={showEvalModal}
        onHide={() => setShowEvalModal(false)}
      />

      <PositionDetailModal
        position={selectedPosition}
        chartData={selectedPosition ? filterChartDataForPosition(liveBars, selectedPosition) : []}
        evaluations={selectedPosition ? filterEvaluationsForPosition(liveBars, selectedPosition) : []}
        show={showPositionModal}
        onHide={handlePositionModalClose}
        onViewFullStory={handleViewFullStory}
      />
    </div>
  );
};

export default TradingCommand;